import { OnboardingStepType, OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import { Card, Container, FOOTER_MAX_HEIGHT, Grid } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { RouteChrome } from '../../routes/RouteChrome'
import { orgRootPath, useOrg } from '../organization/useOrg'
import { buildOnboardingPath, useStepNavigationState } from '../utils/onboardingWorkflowUtils'
import { OnboardingStepTabs } from './OnboardingStepTabs'

type OnboardingLayoutProps = {
  org?: OrganizationDetailFragment | null | undefined
}
/**
 * Main onboarding layout with two-column structure:
 * - Left: Vertical stepper navigation
 * - Right: Main content (header + step content with instructions)
 */
export const OnboardingLayout = ({ org }: OnboardingLayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const onboardingRootPath = buildOnboardingPath(org?.id)
  const { stepConfigs, currentStep } = useStepNavigationState(org)

  const currentStepType = currentStep?.stepType || null

  const handleStepClick = (stepType: OnboardingStepType) => {
    const targetPath = buildOnboardingPath(org?.id, stepType)
    navigate(targetPath)
  }

  if (
    org?.isOnboarded &&
    location.pathname !== buildOnboardingPath(org?.id, OnboardingStepType.SchedulePreshift)
  ) {
    // allow users who have just onboarded to remain on the schedule preshift page
    return <Navigate to={orgRootPath(org.id)} replace />
  }

  return (
    <RouteChrome type="admin" root={{ route: onboardingRootPath, label: 'Home' }}>
      <Container maxWidth="xl">
        <Grid container pt={3} spacing={3} flexWrap="nowrap" paddingBottom={`${FOOTER_MAX_HEIGHT}px`}>
          {/* Left Column - Vertical Stepper */}
          <Grid width="20rem">
            <Card>
              <OnboardingStepTabs
                stepConfigs={stepConfigs}
                currentStepType={currentStepType}
                org={org}
                onStepClick={handleStepClick}
              />
            </Card>
          </Grid>

          {/* Center Column - Main Content */}
          <Grid container flex={3} flexDirection="column">
            <Outlet />
          </Grid>
        </Grid>
      </Container>
    </RouteChrome>
  )
}

export const OrgOnboarding = () => {
  const org = useOrg()
  return <OnboardingLayout org={org} />
}
